import {
	type AnswerGenerationType,
	type ConversationChannelMessageStatus,
	type ConversationChannelStatus,
	type HumanMessage,
	type MessageType,
	type StreamDynamicLayoutSubAgent,
	type StreamFollowUp,
	type StreamResponse,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { type MessageActionInvocationStatus } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import { type StoreAbortController } from '../../common/utils/abort-controller/types';
import { type StoreError } from '../chat-signals';

export enum StoreActionLoggableName {
	addStoreChatItem = 'addStoreChatItem',
	addHumanMessage = 'addHumanMessage',
	addAssistantMessage = 'addAssistantMessage',
	clearFollowUpMessages = 'clearFollowUpMessages',
	sendMessage = 'sendMessage',
	sendMessageConfirmedTools = 'sendMessageConfirmedTools',
	removeMessage = 'removeMessage',
	fetchConversation = 'fetchConversation',
	reconnectToInProgressStream = 'reconnectToInProgressStream',
	abortRequests = 'abortRequests',
	answerNow = 'answerNow',
	addErrorMessage = 'addErrorMessage',
	initConversationChannelsData = 'initConversationChannelsData',
	hydrateConversation = 'hydrateConversation',
	addAgentMessage = 'addAgentMessage',
	handleChatOpenPubsubAction = 'handleChatOpenPubsubAction',
	createConversationOptimistically = 'createConversationOptimistically',
	createSeededConversation = 'createSeededConversation',
	updateActionInvocationStatus = 'updateActionInvocationStatus',
	updateActionData = 'updateActionData',
	resolveConversationAction = 'resolveConversationAction',
	bulkResolveConversationActions = 'bulkResolveConversationActions',
	setSelectedConversation = 'setSelectedConversation',
}

export type ChatStorePrivateState = {
	conversations: {
		[conversationId: string]: Conversation;
	};
	currentConversationId: string;
	liveChatMetadata?: {
		remoteEntityId: string;
	} | null;
};

export type StoreHuman = {
	type: 'HUMAN';
	content: HumanMessage;
	source?: 'TWILIO_LIVE_CHAT';
};

/**
 * Status of the Answer Now feature for fast-forwarding extended thinking.
 * - AVAILABLE: Fast mode response is available, user can click "Answer Now"
 * - SWITCHING: User clicked "Answer Now", waiting for fast mode to be enabled
 */
/**
 * Answer Now status types - discriminated union where AVAILABLE holds both IDs:
 * - tmpMessageId: used for store operations (finding/updating the message)
 * - remoteMessageId: the BE-provided agentMessageId for the API call
 */
export type AnswerNowStatusAvailable = {
	type: 'AVAILABLE';
	tmpMessageId: string;
	remoteMessageId: string;
};
export type AnswerNowStatusOther = {
	type: 'SWITCHING' | 'SWITCHING_REQUESTED' | 'SWITCHED' | 'ERRORED';
};
export type AnswerNowStatus = AnswerNowStatusAvailable | AnswerNowStatusOther;

/** Helper to check if status is AVAILABLE and extract message IDs */
export const isAnswerNowAvailable = (
	status?: AnswerNowStatus,
): status is AnswerNowStatusAvailable => status?.type === 'AVAILABLE';

/** Answer Now status type constants for easier usage */
export const AnswerNowStatusType = {
	AVAILABLE: 'AVAILABLE',
	SWITCHING: 'SWITCHING',
	SWITCHING_REQUESTED: 'SWITCHING_REQUESTED',
	SWITCHED: 'SWITCHED',
	ERRORED: 'ERRORED',
} as const;

export type StoreAssistant = {
	type: 'ASSISTANT';
	content: StreamResponse;
	isAborted?: boolean;
	humanMessageId?: string;

	// Track action invocations against an Agent message
	invokedActions?: {
		[invocationId: string]: MessageActionInvocationStatus;
	};

	markdownData?: Record<string, string>;
	answerGenerationType?: AnswerGenerationType;
	dynamicLayoutSubAgentMessages?: StreamDynamicLayoutSubAgent[];

	/**
	 * Status of the Answer Now feature for this message.
	 * Only set during streaming when extended thinking is active.
	 */
	answerNowStatus?: AnswerNowStatus;
	memory?: StoreMemoryUpdate;
};

export type StoreFollowUp = {
	type: 'FOLLOW_UP';
	content: StreamFollowUp;
	id: number;
	agentMessageId?: string;
	humanMessageId?: string;
};

export type StoreLiveChat = {
	type: 'LIVE_CHAT';
	id: string;
	content: string;
	author?: string;
	timestamp?: string;
	eventType:
		| 'message'
		| 'participant_joined'
		| 'participant_left'
		| 'chat_started'
		| 'system_message'
		| 'chat_closed';
	liveChatConversationId: string;
};

export type StorePhotoCardPreview = {
	type: 'PHOTO_CARD_PREVIEW';
	id: string;
	mediaFileId: string;
	title?: string;
	providerName?: string;
};

export type StoreMessage = StoreHuman | StoreAssistant;

export type StoreTwilioLiveChat = {
	type: 'TWILIO_LIVE_CHAT_AGENT';
	sid: string;
	content: string;
	author: string | null;
	dateCreated: Date | null;
	conversationSid: string;
};

export type StoreMemoryUpdate = {
	type: 'MEMORY_UPDATE';
	content: string;
};

export type StoreChatItem =
	| StoreMessage
	| StoreError
	| StoreFollowUp
	| StoreLiveChat
	| StoreTwilioLiveChat
	| StorePhotoCardPreview
	| StoreMemoryUpdate;

export enum CHAT_STORE_STATUS {
	idle,
	loadingChat,
	awaitingResponse,
	typingResponse,
	deactivatedAgentError,
}

export type MessageTemplate =
	| 'ANALYSING_QUERY'
	| 'CONTENT_SEARCH'
	| 'RESEARCH'
	| 'RESEARCH_STEP_SOURCE'
	| 'RESEARCH_STEP_COMPLETE'
	| 'RESEARCH_STEP_TITLE'
	| 'RESEARCH_STEP_TITLE_SHORT'
	| 'RESEARCH_STEP'
	| 'RESEARCH_START'
	| 'SKILL_START'
	| 'SKILL_COMPLETE'
	| 'PAGE_HYDRATION'
	| 'WRITING'
	| 'ERROR'
	| 'ACCEPTABLE_USE_VIOLATIONS'
	| 'MEMORY_UPDATE'
	| 'MEMORY_SEARCH'
	| 'EXECUTING_PLUGIN'
	| 'PLUGIN_INVOCATION_INFO'
	| 'AGENT_EXECUTE'
	| 'MCP_UNAUTHORIZED'
	| 'THINK_DEEPER_TRIGGERED';

export type Trace = {
	messageTemplate?: MessageTemplate;
	messageContent: string;
	// TODO: sync type with platform/packages/ai-mate/conversation-assistant-ui-components/src/common/ui/loading-status-indicator/index.tsx
	status?: 'loading' | 'success' | 'error';
};

export type Conversation = {
	id: string;
	name: string;
	/** @private
	 * @deprecated use `useChatMessages` hook instead
	 *
	 * If you are inside `/ai-mate/conversation-assistant/` DO NOT USE THIS FIELD, it will always be empty.
	 */
	messages: StoreChatItem[];
	status: CHAT_STORE_STATUS;
	traces: Trace[];
	storeAbortController?: StoreAbortController;
	timeCreated: string;
	sourceId?: string;
	lastMessageTimestamp?: string;
	lastMessage?: string;
	lastMessageType?: MessageType[];
	lastAgentMessageStatus?: ConversationChannelMessageStatus;
	sourceAgentId?: string;
	channelStatus?: ConversationChannelStatus;
	agent?: AgentMetadata;
	errorCode?: string;
	readStatus?: boolean | null;
};

export type AgentMetadata = {
	agent_id: string;
	agent_named_id: string;
	agent_name: string;
	identity_account_id?: string;
	creator_type: string;
	forge_url?: string;
	is_default?: boolean;
};

export const isStoreHuman = (message: StoreChatItem): message is StoreHuman =>
	'type' in message && message.type === 'HUMAN';

export const isStoreAssistant = (message: StoreChatItem): message is StoreAssistant =>
	'type' in message && message.type === 'ASSISTANT';

export const isStoreError = (message: StoreChatItem): message is StoreError =>
	'type' in message && message.type === 'ERROR';

export const isStoreMessage = (message: StoreChatItem): message is StoreMessage =>
	isStoreHuman(message) || isStoreAssistant(message);

export const isStoreMemoryUpdate = (message: StoreChatItem): message is StoreMemoryUpdate =>
	'type' in message && message.type === 'MEMORY_UPDATE';

export const isStoreFollowUp = (message: StoreChatItem): message is StoreFollowUp =>
	'type' in message && message.type === 'FOLLOW_UP';

export const isStoreLiveChat = (message: StoreChatItem): message is StoreLiveChat =>
	'type' in message && message.type === 'LIVE_CHAT';

export const isStoreTwilioLiveChatAgent = (
	message: StoreChatItem,
): message is StoreTwilioLiveChat => 'type' in message && message.type === 'TWILIO_LIVE_CHAT_AGENT';

export const isStoreTwilioLiveChatHuman = (
	message: StoreChatItem,
): message is StoreHuman & { source: 'TWILIO_LIVE_CHAT' } =>
	isStoreHuman(message) && message.source === 'TWILIO_LIVE_CHAT';

export const isStoreTwilioLiveChatMessage = (message: StoreChatItem): boolean =>
	isStoreTwilioLiveChatAgent(message) || isStoreTwilioLiveChatHuman(message);

export const isStorePhotoCardPreview = (message: StoreChatItem): message is StorePhotoCardPreview =>
	'type' in message && message.type === 'PHOTO_CARD_PREVIEW';

export const getActionInvocationStatus = (
	invocationId: string,
	invokedActions: StoreAssistant['invokedActions'],
): MessageActionInvocationStatus | 'idle' =>
	(invokedActions && invokedActions[invocationId]) || 'idle';
